import { List, Map, Record } from "immutable";
import { fromJSON, JobRecord } from "common/records/Job";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { Nullable } from "common/types";
import { RootActions, RootState } from "common/reducers";
import { reducer as orgReducer } from "common/reducers/org.reducer";
import { reducer as estimateComparisonReducer } from "common/reducers/estimateComparison.reducer";
import { EstimateComparisonActions } from "common/reducers/estimateComparison.actions";
import { reducer as rsfFormReducer } from "common/reducers/rsfForm.reducer";
import { OrgActions } from "common/reducers/org.actions";
import {
  ADD_PHONE_NUMBER,
  FETCH_JOB,
  JobActions,
  RECEIVE_JOB,
  RECEIVE_JOB_OVERVIEWS,
  RECEIVE_JOB_PARAMS,
  RECEIVE_JOB_STATES,
  REMOVE_PHONE_NUMBER,
  RESET_JOB,
  RESET_JOB_PARAMS,
  SET_CURRENT_JOB_ID,
  SET_JOB_ERRORS,
  SET_JOB_LOADED,
  SET_SAMPLE_JOBS,
  TOGGLE_JOB_ARCHIVED,
  UPDATE_JOB_PARAMS,
  UPDATE_JOB_CREATE_FORM,
  SET_COVER_IMAGE,
  EDIT_RESOURCE_DISPLAY,
  SET_RESOURCE_DOCUMENTS,
  SET_JOB_ADDRESS,
  RECEIVE_ORG_JOB_PARAMS,
} from "common/reducers/job.actions";
import { currentJob, currentJobId } from "common/selectors/job.selectors";
import { EstimateActions } from "common/reducers/estimate.actions";
import { reducer as estimateReducer } from "common/reducers/estimate.reducer";
import { PhoneNumberRecord, phoneFromJSON } from "common/records/PhoneNumber";
import { reducer as userReducer } from "common/reducers/user.reducer";
import { UserActions } from "common/reducers/user.actions";
import { JobTypeActions } from "common/reducers/jobType.actions";
import { reducer as jobTypeReducer } from "common/reducers/jobType.reducer";
import { calculateUpdatedBefore, calculateUpdatedSince, jobParamsFromJSON } from "common/records/index/JobParams";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { capitalizeFirst } from "common/helpers/String";

export const JobStateRecord = Record({
  // Job ID of the current view
  currentJobId: null as Nullable<number>,
  byId: Map<number, JobRecord>(),
  lastSavedById: Map<number, JobRecord>(),
  sampleJobs: List<string>(),
  overviewsById: Map<number, JobRecord>(),
  jobStates: List<Record<{ id: string; label: string; color: string }>>(),
  jobParams: jobParamsFromJSON({}),
  lastSavedJobParams: jobParamsFromJSON({}),
  orgJobParams: jobParamsFromJSON({}),
  lastSavedOrgJobParams: jobParamsFromJSON({}),
});

export const initialState = JobStateRecord();

export type JobState = typeof initialState;

export const initialJobRecord = Record({ jobs: initialState })();
export type JobRootRecord = typeof initialJobRecord;

export const model = "jobs";
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case SET_CURRENT_JOB_ID:
      return state.setIn([model, "currentJobId"], action.payload.id);
    case FETCH_JOB:
      state = fetch(state, model, fromJSON({ id: action.payload.id }));
      return reducer(state, JobActions.setJobErrors(action.payload.id, []));
    case RECEIVE_JOB:
      const { estimate_comparison: sideLoadedComparison, ...jobData } = action.payload.job;
      const job = fromJSON({ ...jobData });
      if (jobData.org) {
        state = orgReducer(state, OrgActions.receiveOrg(jobData.org));
      }

      if (sideLoadedComparison?.id) {
        state = estimateComparisonReducer(
          state,
          EstimateComparisonActions.receiveEstimateComparison(sideLoadedComparison),
        );
      }

      if (action.payload.job.estimates) {
        state = estimateReducer(state, EstimateActions.receiveEstimateOverviews(action.payload.job.estimates));
      }

      if (jobData.assignments && jobData.assignments[0]?.user) {
        state = userReducer(state, UserActions.receiveUser(jobData.assignments[0].user));
      }
      if (jobData.job_types) {
        state = jobTypeReducer(state, JobTypeActions.receiveJobTypes(jobData.job_types));
      }

      return receive(state, model, job) as RootState;
    case SET_JOB_LOADED:
      return loaded(state, model, action.payload.id) as RootState;

    case SET_JOB_ERRORS: {
      const jobId = action.payload.id;
      const errors = action.payload.errors;
      if (state.getIn([model, "byId", jobId])) {
        return state.setIn([model, "byId", jobId, "errors"], List(errors));
      }
      return state.setIn([model, "byId", jobId], fromJSON({ errors }));
    }

    case SET_SAMPLE_JOBS:
      return state.setIn([model, "sampleJobs"], List(action.payload.sampleJobs));
    case RECEIVE_JOB_OVERVIEWS: {
      action.payload.jobs.forEach((job) => {
        const record = fromJSON(job);
        state = state.setIn([model, "overviewsById", record.id], record);
      });
      return state;
    }
    case RECEIVE_JOB_STATES:
      const jobStates = List(
        Object.entries(action.payload.jobStates).map(([key, data]: [string, any]) => {
          return Record({ id: key, label: data.pretty_name, color: data.color })();
        }),
      );
      return state.setIn([model, "jobStates"], jobStates);

    case ADD_PHONE_NUMBER: {
      const jobId = currentJobId(state);
      return state.updateIn([model, "byId", jobId, "phone_numbers"], (phoneNumbers) => {
        return phoneNumbers.push(phoneFromJSON({}));
      });
    }

    case REMOVE_PHONE_NUMBER: {
      const jobId = currentJobId(state);
      return state.updateIn([model, "byId", jobId, "phone_numbers"], (phoneNumbers: List<PhoneNumberRecord>) => {
        // If it's new just remove it
        if (phoneNumbers.get(action.payload.index)?.id === 0) {
          phoneNumbers = phoneNumbers.remove(action.payload.index);
        } else {
          phoneNumbers = phoneNumbers.setIn([action.payload.index, "_destroy"], true);
        }

        // If there are no phone numbers left, add a blank one
        if (phoneNumbers.filter((pn) => !pn?._destroy).size === 0) {
          phoneNumbers = phoneNumbers.push(phoneFromJSON({}));
        }

        return phoneNumbers;
      });
    }

    case RESET_JOB: {
      const jobId = currentJobId(state);
      if (!jobId) {
        return state;
      }
      return reset(state, "jobs", jobId);
    }

    case TOGGLE_JOB_ARCHIVED: {
      const jobId = action.payload.jobId;
      const job = state.getIn([model, "byId"]).get(jobId);

      return state
        .setIn([model, "byId", jobId], job?.set("archived", !job?.archived))
        .setIn([model, "lastSavedById", jobId], job?.set("archived", !job?.archived));
    }

    case RECEIVE_JOB_PARAMS:
      const { jobParams } = action.payload;
      return state
        .setIn([model, "jobParams"], jobParamsFromJSON(jobParams))
        .setIn([model, "lastSavedJobParams"], jobParamsFromJSON(jobParams));

    case RECEIVE_ORG_JOB_PARAMS:
      const { jobParams: orgJobParams } = action.payload;
      return state
        .setIn([model, "orgJobParams"], jobParamsFromJSON(orgJobParams))
        .setIn([model, "lastSavedOrgJobParams"], jobParamsFromJSON(orgJobParams));

    case UPDATE_JOB_PARAMS: {
      const { rootPath, name, value } = action.payload.event;

      state = rsfFormReducer(state, RsfFormActions.update(action.payload.event));

      if (name === "org_id") {
        state = state.setIn(rootPath.concat(["user_id"]), null);
        state = state.setIn(rootPath.concat(["job_type"]), null);
        state = state.setIn(rootPath.concat(["lead_source"]), null);
        state = state.setIn(rootPath.concat(["state"]), null);
      }

      if (name === "date_range" && value !== "custom") {
        const jobParams = state.getIn(rootPath);
        state = state.setIn(rootPath.concat(["updated_since"]), calculateUpdatedSince(jobParams));
        state = state.setIn(rootPath.concat(["updated_before"]), calculateUpdatedBefore(jobParams));
      }
      return state;
    }

    case RESET_JOB_PARAMS: {
      const jobParamsType = action.payload.jobParamsType;

      return state.setIn([model, jobParamsType], state.getIn([model, `lastSaved${capitalizeFirst(jobParamsType)}`]));
    }

    case UPDATE_JOB_CREATE_FORM: {
      const { event } = action.payload;
      const { rootPath, name, value } = event;
      state = rsfFormReducer(state, RsfFormActions.update(event));
      const job = currentJob(state);

      if (name === "name") {
        state = state.setIn(rootPath.concat(["jobNameChanged"]), true);
      }

      if (!job?.jobNameChanged && name === "customer_name") {
        const jobId = currentJobId(state);
        state = state.setIn([model, "byId", jobId, "name"], value);
      }
      return state;
    }

    case SET_COVER_IMAGE: {
      const jobId = currentJobId(state);
      return state.setIn([model, "byId", jobId, "cover_image_uuid"], action.payload.imageUuid);
    }

    case EDIT_RESOURCE_DISPLAY:
      let documents = state.getIn(
        [model, "byId", action.payload.jobId, "config", "resource_documents"],
        List<any>(),
      ) as List<any>;
      const foundIndex = documents.findIndex((d: any) => d.get("id") === action.payload.documentId);
      if (foundIndex < 0) {
        let doc = Map({ id: action.payload.documentId, displayInAgreement: false, displayInProposal: false });
        doc = doc.set(action.payload.name, action.payload.value);
        documents = documents.push(doc);
      } else {
        documents = documents.setIn([foundIndex, action.payload.name], action.payload.value);
      }

      documents = documents.filter((doc: any) => {
        return doc.get("displayInAgreement") || doc.get("displayInProposal");
      });

      return state.setIn([model, "byId", action.payload.jobId, "config", "resource_documents"], documents);

    case SET_RESOURCE_DOCUMENTS:
      return state.setIn(
        [model, "byId", action.payload.jobId, "config", "resource_documents"],
        action.payload.resourceDocuments,
      );

    case SET_JOB_ADDRESS:
      const jobId = currentJobId(state);
      return state.setIn([model, "byId", jobId, "address"], action.payload.addressRecord);

    default:
      return state;
  }
};
